import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import Lightbox from "@/galleries/shared/Lightbox";
import { useReveal } from "@/hooks/useReveal";
import { InViewTextEffect } from "@/components/ui/text-effect";

// Typographic hierarchy borrowed from Internet Archive film-catalog pages:
// title / year / duration stacked in mono, thumbnail treated as a still
// rather than a hero — the label carries the weight, not the image.
// Stand-in embeds only — these are unrelated public YouTube clips used to
// prove the showcase mechanics (hover preview, modal, mobile snap-scroll).
// Swap youtubeId/thumb for real client footage before shipping; until then
// the labels say "placeholder" rather than naming a client against footage
// that isn't theirs.
const VIDEOS = [
  {
    id: "placeholder-1",
    client: "Placeholder reel 01",
    project: "Swap for real client footage",
    year: "—",
    youtubeId: "aqz-KE-bpKQ",
    thumb: "https://img.youtube.com/vi/aqz-KE-bpKQ/hqdefault.jpg",
  },
  {
    id: "placeholder-2",
    client: "Placeholder reel 02",
    project: "Swap for real client footage",
    year: "—",
    youtubeId: "5qap5aO4i9A",
    thumb: "https://img.youtube.com/vi/5qap5aO4i9A/hqdefault.jpg",
  },
  {
    id: "placeholder-3",
    client: "Placeholder reel 03",
    project: "Swap for real client footage",
    year: "—",
    youtubeId: "8pOgLIB1WFU",
    thumb: "https://img.youtube.com/vi/8pOgLIB1WFU/hqdefault.jpg",
  },
  {
    id: "placeholder-4",
    client: "Placeholder reel 04",
    project: "Swap for real client footage",
    year: "—",
    youtubeId: "ysz5S6PUM-U",
    thumb: "https://img.youtube.com/vi/ysz5S6PUM-U/hqdefault.jpg",
  },
];

function VideoCard({ video, onOpen }) {
  const [hovering, setHovering] = useState(false);
  const showPreview = hovering && typeof window !== "undefined" && window.innerWidth >= 768;

  return (
    <div
      data-anim
      className="w-full flex-shrink-0 snap-start md:flex-shrink md:snap-none"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <button
        type="button"
        onClick={() => onOpen(video)}
        className="group relative block aspect-video w-full overflow-hidden rounded-lg bg-ink"
      >
        {showPreview ? (
          <iframe
            title={`${video.client} preview`}
            src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${video.youtubeId}&playsinline=1`}
            className="pointer-events-none h-full w-full"
            allow="autoplay; encrypted-media"
          />
        ) : (
          // Oversized + absolutely positioned so the data-parallax translate
          // (±~7% of card height at speed 0.15) never exposes the bg-ink
          // edge underneath inside this overflow-hidden frame.
          <img
            data-parallax="0.15"
            src={video.thumb}
            alt={video.project}
            loading="lazy"
            className="absolute inset-x-0 -top-[8%] h-[116%] w-full object-cover opacity-90 transition group-hover:opacity-70"
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-paper/90 text-pine shadow-lg transition group-hover:scale-105">
            <Play size={22} strokeWidth={1.5} fill="currentColor" />
          </span>
        </div>
      </button>
      <div className="mt-3 font-mono text-xs uppercase tracking-[0.12em] text-ink-dim">
        <span className="text-ink">{video.client}</span>
        <span className="mx-2 text-hairline">/</span>
        {video.project}
        <span className="mx-2 text-hairline">/</span>
        {video.year}
      </div>
    </div>
  );
}

export default function VideoShowcase() {
  const [active, setActive] = useState(null);
  const gridRef = useRef(null);
  useReveal(gridRef);

  const items = active
    ? [
        {
          caption: `${active.client} — ${active.project}`,
          render: () => (
            <div className="aspect-video w-[min(80vw,960px)]">
              <iframe
                title={active.project}
                src={`https://www.youtube.com/embed/${active.youtubeId}?rel=0`}
                className="h-full w-full rounded-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ),
        },
      ]
    : [];

  return (
    <div className="mx-auto max-w-6xl px-5 py-14 md:px-8">
      <header className="mb-10 max-w-xl">
        <InViewTextEffect
          as="p"
          preset="blur"
          className="font-mono text-xs uppercase tracking-[0.18em] text-pine/70"
        >
          Process &amp; Motion
        </InViewTextEffect>
        <h2 className="mt-2 font-serif text-3xl text-ink md:text-4xl">
          Watch the work happen
        </h2>
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-rust">
          Stand-in clips — layout demo only, swap for real client footage
        </p>
      </header>

      <div
        ref={gridRef}
        data-stagger
        className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 md:grid md:grid-cols-2 md:overflow-visible md:pb-0"
      >
        {VIDEOS.map((video) => (
          <VideoCard key={video.id} video={video} onOpen={setActive} />
        ))}
      </div>

      <Lightbox items={items} index={active ? 0 : null} onClose={() => setActive(null)} onNav={() => {}} />
    </div>
  );
}
